// "Add-up" pricing — the price of a product is its base price plus a per-part price
// for each option value the customer picks (fixed panel width + door width + hardware
// colour + …). It exists because per-part products explode combinatorially: five parts
// with 15/10/15/21/6 values is 958,500 variation rows, but only 67 part prices.
//
// The per-value prices live in the existing products.options JSON as a `prices` map:
//   [{ name: "Fixed Panel Width (mm)", values: ["800", …], prices: { "800": 120, … } }]
// so nothing about the DB schema, order snapshots or the storefront's option steps
// changes. A product is in add-up mode when any option carries a non-empty prices map.

export const isAddUp = (options) =>
  (options || []).some((o) => o && o.prices && Object.keys(o.prices).length > 0)

// price added by one chosen value (an unpriced value — e.g. "None" — adds nothing)
export const addOn = (opt, value) => {
  const n = Number(opt?.prices?.[value])
  return Number.isFinite(n) ? n : 0
}

// total of every part the customer has chosen so far
export const addOnTotal = (options, selected) =>
  (options || []).reduce((sum, o) => sum + (selected[o.name] ? addOn(o, selected[o.name]) : 0), 0)

// ---- admin input format -------------------------------------------------------
// The owner types parts in the same comma-separated box as before, adding "=price":
//   800=120, 850=130, None=0
// A value with no "=" simply adds nothing, so plain option lists keep working.

export function parseValueSpec(text) {
  const values = []
  const prices = {}
  String(text || '').split(',').forEach((chunk) => {
    const raw = chunk.trim()
    if (!raw) return
    const eq = raw.indexOf('=')
    if (eq === -1) { values.push(raw); return }
    const name = raw.slice(0, eq).trim()
    const price = Number(raw.slice(eq + 1).trim())
    if (!name) return
    values.push(name)
    if (Number.isFinite(price)) prices[name] = price
  })
  return { values, prices }
}

// render an option back into that box; add-up products always show an explicit price
// (including 0) so the owner can see at a glance which parts are free
export function formatValueSpec(values, prices, addUpMode) {
  return (values || [])
    .map((v) => {
      if (!addUpMode) return v
      const n = Number(prices?.[v])
      return `${v}=${Number.isFinite(n) ? n : 0}`
    })
    .join(', ')
}
